import { useStore } from '@nanostores/react';
import { useRouter } from '@tanstack/react-router';
import { lazy, Suspense, type JSX } from 'react';
import { useLocation } from '@/services/router/compat';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { updateBrandWizardDraft } from '../wizard/draft-store';
import { isBrandRunBannerRoute, isBrandStylesRoute, STYLES_ROUTE } from './banner-routes';
import { isBrandRunInProgress, type BrandRunState } from './brand-run-state';
import { useHasResumableBrandRun } from './resumable-run';
import { requestBrandRunIntent, type BrandRunIntentKind } from './run-intent';
import { useBrandRunTracker } from './use-brand-run-tracker';
import { $brandWizardTakeover } from './wizard-presence';

const LazyBrandRunSnackbar = lazy(() => import('./brand-run-snackbar').then((m) => ({ default: m.BrandRunSnackbar })));

/**
 * The intent a click on the snackbar's action requests, per state. "Review"
 * should land IN the review, and "View" on a run that is writing in its
 * progress view, so those request the intent the wizard consumes as it mounts.
 * Every other "View" stays a plain navigation: sending that state's banner
 * intent would retry or restart a run on a click that only said "take me there".
 */
const SNACKBAR_INTENT = {
  extracting: null,
  extract_ready: 'review',
  extract_failed: null,
  authoring: 'enter',
  ready: 'enter',
  failed: null,
  sources_expired: null,
  stalled: null,
} satisfies Record<BrandRunState['kind'], BrandRunIntentKind | null>;

/** What the snackbar's action does, or `undefined` when there is nothing to open. */
function actionFor(
  state: BrandRunState,
  workspaceId: number,
  isStylesPage: boolean,
  goToStyles: () => void,
): (() => void) | undefined {
  const intent = SNACKBAR_INTENT[state.kind];
  // Reading has no progress view to open once you are on Styles.
  if (intent == null) return state.kind === 'extracting' && isStylesPage ? undefined : goToStyles;
  return () => {
    requestBrandRunIntent(intent, workspaceId);
    // On Styles the wizard is already mounted and acts on the intent in place.
    if (!isStylesPage) goToStyles();
  };
}

type HostBodyProps = {
  workspaceId: number;
  /** A Design Studio page, whose banner carries every state but a run still working. */
  isDesignStudio: boolean;
  /** The Styles page, where the wizard is mounted to act on an intent in place. */
  isStylesPage: boolean;
};

function HostBody({ workspaceId, isDesignStudio, isStylesPage }: HostBodyProps): JSX.Element | null {
  const { draft, state } = useBrandRunTracker(workspaceId);
  const isWizardTakeover = useStore($brandWizardTakeover);
  const safeNavigate = useSafeNavigate();
  const router = useRouter();
  if (draft == null || state == null) return null;
  if (isDesignStudio) {
    // The only surface there for a run still working, so a dismissal made on
    // any page cannot hide it. A takeover already shows the run.
    if (!isBrandRunInProgress(state) || isWizardTakeover) return null;
  } else if (draft.toastDismissed === true) {
    return null;
  }
  const goToStyles = () =>
    safeNavigate(router.buildLocation({ to: STYLES_ROUTE, params: { workspaceId: String(workspaceId) } }).href);
  return (
    <Suspense fallback={null}>
      <LazyBrandRunSnackbar
        state={state}
        onAction={actionFor(state, workspaceId, isStylesPage, goToStyles)}
        onClose={
          isDesignStudio
            ? undefined
            : () => updateBrandWizardDraft({ toastDismissed: true }, { workspaceId, authorRun: draft.authorRun })
        }
      />
    </Suspense>
  );
}

/**
 * Mounted once in the authenticated layout. Renders nothing (and loads nothing
 * extra) unless this tab holds a brand generation for the current workspace.
 * On the Design Studio pages, whose banner above the masthead carries the
 * states that need a click, it only covers a run that is still working.
 */
export function BrandRunSnackbarHost(): JSX.Element | null {
  const { pathname } = useLocation();
  const workspaceId = getWorkspaceIdFromCurrentUrl(pathname);
  // The same predicate the Styles page mounts its wizard on, so the two
  // surfaces never disagree about whether there is a run to show.
  const hasRun = useHasResumableBrandRun(workspaceId);
  if (!hasRun || workspaceId == null) return null;
  return (
    <HostBody
      workspaceId={workspaceId}
      isDesignStudio={isBrandRunBannerRoute(pathname)}
      isStylesPage={isBrandStylesRoute(pathname)}
    />
  );
}
